import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { MagnifyingGlass, Buildings, ArrowRight, CaretDown, MapPin, MagnifyingGlassPlus, Check, X, ArrowsClockwise, SealCheck, Trophy, ChartBar, Sparkle, CircleNotch, Plus, Globe, ArrowSquareOut, CurrencyInr } from "@phosphor-icons/react";
import { getColleges, getFilterOptions, formatFees, findDuplicates, findDuplicatesStatus, mergeDuplicates, fillNewCollegesPreview, runFillNewColleges, jobsStatus, addMasterFees, feesLlmCheck, feesLlmApply, fetchFeesOfficial, fetchFeesOfficialJob, addFeeSourceUrl, exportFeesCsv, ASSET_BASE } from "../lib/api";
import { CollegeLogo as SharedCollegeLogo } from "../components/CollegeLogo";
import { NewsChannel } from "../components/NewsChannel";
import { useAuth } from "../lib/AuthProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { value: "govt", label: "Government" },
  { value: "private", label: "Private" },
  { value: "trust", label: "Trust" },
  { value: "deemed", label: "Deemed" },
];

const FEE_BUCKET_LABELS = { govt: "GOVT FEES", private: "PRIVATE FEES", other: "MGMT FEES", nri: "NRI FEES" };

function MultiSelectType({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length === 0 ? "All Types"
    : value.length === 1 ? (TYPE_OPTIONS.find((t) => t.value === value[0])?.label || value[0])
    : `${value.length} types`;
  return (
    <div className="relative w-[150px]" ref={ref} data-testid="college-type-filter">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center justify-between gap-1 rounded-md border border-zinc-300 bg-white px-3 text-sm text-[#09090B] hover:border-[#6043EF] transition-colors">
        <span className={`truncate ${value.length ? "font-medium" : "text-zinc-500"}`}>{label}</span>
        <CaretDown size={13} weight="bold" className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg py-1" data-testid="college-type-options">
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              className="w-full text-left px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-600 hover:bg-rose-50">Clear</button>
          )}
          {TYPE_OPTIONS.map((t) => {
            const on = value.includes(t.value);
            return (
              <button key={t.value} type="button" onClick={() => toggle(t.value)} data-testid={`type-opt-${t.value}`}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-zinc-50">
                <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? "bg-[#6043EF] border-[#6043EF]" : "border-zinc-300"}`}>
                  {on && <Check size={11} weight="bold" className="text-white" />}
                </span>
                {t.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function CollegeList() {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const urlState = searchParams.get("state");
  const urlQ = searchParams.get("q");
  const urlCourse = searchParams.get("course");
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => urlQ || localStorage.getItem("cl_search") || "");
  const [state, setState] = useState(() => urlState || localStorage.getItem("cl_state") || "All");
  const [course, setCourse] = useState(() => urlCourse || localStorage.getItem("cl_course") || "all");
  const [collegeTypes, setCollegeTypes] = useState(() => { try { return JSON.parse(localStorage.getItem("cl_types") || "[]"); } catch { return []; } });
  const [feeBucket, setFeeBucket] = useState(() => localStorage.getItem("cl_fee_bucket") || "none");
  const [spotlight, setSpotlight] = useState(() => localStorage.getItem("cl_spotlight") || "all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const [filterOptions, setFilterOptions] = useState({ states: [], counselling_bodies: [], courses: [] });
  const [expandedRow, setExpandedRow] = useState(null);
  const [dedupGroups, setDedupGroups] = useState(null);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [fillList, setFillList] = useState(null);      // null | [{college_code,name,city,missing}]
  const [fillLoading, setFillLoading] = useState(false);
  const [fillJobs, setFillJobs] = useState({});        // code -> job_id
  const [fillStatuses, setFillStatuses] = useState({}); // code -> queued|running|completed|failed
  const [fillRunning, setFillRunning] = useState(false);
  const [feesDialog, setFeesDialog] = useState(null); // null | college object (manual add)
  const [aiFeesDialog, setAiFeesDialog] = useState(null); // null | college object (LLM fetch)
  const [officialFeesDialog, setOfficialFeesDialog] = useState(null); // null | college object (official scrape)
  const [exportOpen, setExportOpen] = useState(false);
  const [exportAliases, setExportAliases] = useState("off");
  const [exportCodes, setExportCodes] = useState("single");
  const [exportFields, setExportFields] = useState(() => new Set(["fees", "seats"]));
  const [exporting, setExporting] = useState(false);
  const limit = 25;

  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem("cl_search", search);
    localStorage.setItem("cl_state", state);
    localStorage.setItem("cl_course", course);
    localStorage.setItem("cl_types", JSON.stringify(collegeTypes));
    localStorage.setItem("cl_fee_bucket", feeBucket);
    localStorage.setItem("cl_spotlight", spotlight);
  }, [search, state, course, collegeTypes, feeBucket, spotlight]);

  useEffect(() => {
    getFilterOptions().then((res) => setFilterOptions(res.data)).catch(() => {});
  }, []);

  const loadColleges = async (pageToLoad = 1, append = false) => {
    if (append) setLoadingMore(true); else { setLoading(true); setExpandedRow(null); }
    try {
      const params = { page: pageToLoad, limit };
      if (search) params.search = search;
      if (state !== "All") params.state = state;
      if (course !== "all") params.course = course;
      if (collegeTypes.length) params.college_type = collegeTypes.join(",");
      if (feeBucket !== "none") params.fee_bucket = feeBucket;
      if (spotlight !== "all") params.spotlight = spotlight;
      const res = await getColleges(params);
      const rows = res.data.colleges || [];
      setColleges((prev) => (append ? [...prev, ...rows] : rows));
      setTotal(res.data.total || 0);
      setPage(pageToLoad);
    } catch (e) {
      console.error("Failed to load colleges", e);
    }
    if (append) setLoadingMore(false); else setLoading(false);
  };

  // Any filter change → reset to page 1 (replaces the list, not appends)
  useEffect(() => {
    loadColleges(1, false);
  }, [state, course, collegeTypes, feeBucket, spotlight]); // eslint-disable-line

  // Live search — debounced 350ms; only fires at 3+ chars (empty resets to full list)
  useEffect(() => {
    const q = search.trim();
    if (q.length > 0 && q.length < 3) return; // ignore 1-2 char noise
    const t = setTimeout(() => loadColleges(1, false), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  // Sync filters when arriving via a mega-menu SEO link (URL params change)
  useEffect(() => {
    if (urlState) setState(urlState);
    if (urlQ !== null) setSearch(urlQ || "");
    if (urlCourse) setCourse(urlCourse);
  }, [urlState, urlQ, urlCourse]); // eslint-disable-line

  // Infinite scroll — load the next page when the sentinel scrolls into view
  const hasMore = colleges.length < total;
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadColleges(page + 1, true); },
      { rootMargin: "400px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, loadingMore, page]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    loadColleges(1, false);
  };

  return (
    <div className="min-h-screen" data-testid="college-list-page">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-1">DATABASE</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading">
            All Colleges
          </h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="college-filters">
          <div className="flex-1 min-w-[200px]">
            <form onSubmit={handleSearch} className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search colleges..."
                className="pl-8 border-zinc-300 focus:border-[#6043EF]"
                data-testid="college-search-input"
              />
            </form>
          </div>
          <div className="w-[140px]">
            <Select value={state} onValueChange={(v) => { setState(v); setPage(1); }}>
              <SelectTrigger className="border-zinc-300" data-testid="college-state-filter">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All States</SelectItem>
                {filterOptions.states.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[150px]">
            <Select value={course} onValueChange={(v) => { setCourse(v); setPage(1); }}>
              <SelectTrigger className="border-zinc-300" data-testid="college-course-filter">
                <SelectValue placeholder="Course" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {(filterOptions.courses || []).map((c) => (
                  <SelectItem key={c} value={c}>{COURSE_LABELS[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <MultiSelectType value={collegeTypes} onChange={(v) => {
            // Auto-pick the fees column from the college-type selection:
            //  • single "govt"    → Govt fees
            //  • single "private" → Private fees
            //  • multi-select     → Private fees, but only if no fees column is chosen yet
            if (v.length > 1) {
              if (feeBucket === "none") setFeeBucket("private");
            } else if (v.length === 1) {
              if (v[0] === "govt") setFeeBucket("govt");
              else if (v[0] === "private") setFeeBucket("private");
            }
            setCollegeTypes(v); setPage(1);
          }} />
          <div className="w-[150px]">
            <Select value={feeBucket} onValueChange={(v) => { setFeeBucket(v); setPage(1); }}>
              <SelectTrigger className="border-zinc-300" data-testid="college-fees-filter">
                <SelectValue placeholder="Fees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No fees column</SelectItem>
                <SelectItem value="govt">Govt fees</SelectItem>
                <SelectItem value="private">Private/Regular fees</SelectItem>
                <SelectItem value="other">Management/Other fees</SelectItem>
                <SelectItem value="nri">NRI fees</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2" data-testid="spotlight-chips">
            <button
              type="button"
              onClick={() => { setSpotlight(spotlight === "ni" ? "all" : "ni"); setPage(1); }}
              aria-pressed={spotlight === "ni"}
              data-testid="chip-spotlight-ni"
              className={`flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors duration-150 ${
                spotlight === "ni"
                  ? "bg-[#6043EF] border-[#6043EF] text-white"
                  : "bg-white border-zinc-300 text-[#52525B] hover:border-[#6043EF] hover:text-[#6043EF]"
              }`}
            >
              <SealCheck size={14} weight={spotlight === "ni" ? "fill" : "bold"} />
              National Importance
              {spotlight === "ni" && <X size={12} weight="bold" className="ml-0.5 opacity-80" />}
            </button>
            <button
              type="button"
              onClick={() => { setSpotlight(spotlight === "nirf" ? "all" : "nirf"); setPage(1); }}
              aria-pressed={spotlight === "nirf"}
              data-testid="chip-spotlight-nirf"
              className={`flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors duration-150 ${
                spotlight === "nirf"
                  ? "bg-[#F59E0B] border-[#F59E0B] text-black"
                  : "bg-white border-zinc-300 text-[#52525B] hover:border-[#F59E0B] hover:text-[#B45309]"
              }`}
            >
              <Trophy size={14} weight={spotlight === "nirf" ? "fill" : "bold"} />
              Top NIRF
              {spotlight === "nirf" && <X size={12} weight="bold" className="ml-0.5 opacity-80" />}
            </button>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {state !== "All" && (<>
              <button onClick={async () => {
                  setDedupLoading(true); setDedupGroups(null);
                  try {
                    const res = await findDuplicates(state);
                    const data = res.data;
                    if (data.status === "done") {
                      setDedupGroups(data.groups || []);
                      setDedupLoading(false);
                    } else if (data.job_id) {
                      // Poll for results
                      const poll = setInterval(async () => {
                        try {
                          const pollRes = await findDuplicatesStatus(data.job_id);
                          if (pollRes.data.status === "done") {
                            clearInterval(poll);
                            setDedupGroups(pollRes.data.groups || []);
                            setDedupLoading(false);
                          } else if (pollRes.data.status === "failed") {
                            clearInterval(poll);
                            setDedupGroups([]);
                            setDedupLoading(false);
                          }
                        } catch (e) { clearInterval(poll); setDedupLoading(false); }
                      }, 3000);
                    }
                  } catch (e) { console.error(e); setDedupLoading(false); }
                }}
                disabled={dedupLoading}
                className="h-7 px-2 border border-[#FFC107] text-[#92700C] text-[9px] font-bold uppercase tracking-wider hover:bg-[#FFC107]/10 disabled:opacity-50 flex items-center gap-1 shrink-0"
                data-testid="find-duplicates-btn">
                <MagnifyingGlassPlus size={11} /> {dedupLoading ? "..." : "DEDUP"}
              </button>
              <button onClick={async () => {
                  setFillLoading(true); setFillList(null); setFillJobs({}); setFillStatuses({});
                  try {
                    const res = await fillNewCollegesPreview(state);
                    setFillList(res.data.colleges || []);
                  } catch (e) { console.error(e); }
                  setFillLoading(false);
                }}
                disabled={fillLoading}
                className="h-7 px-2 border border-[#6043EF] text-[#4A28C2] text-[9px] font-bold uppercase tracking-wider hover:bg-[#6043EF]/10 disabled:opacity-50 flex items-center gap-1 shrink-0"
                data-testid="fill-new-btn">
                <Sparkle size={11} /> {fillLoading ? "..." : "FILL NEW"}
              </button>
            </>)}
            {isAdmin && (
              <button onClick={() => setExportOpen(true)}
                className="h-7 px-2 border border-[#0EA5E9] text-[#0369A1] text-[9px] font-bold uppercase tracking-wider hover:bg-[#0EA5E9]/10 flex items-center gap-1 shrink-0"
                data-testid="export-fees-btn">
                EXPORT COLLEGE DATA
              </button>
            )}
            <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">{total} colleges</span>
          </div>
        </div>

        {/* Export Fees CSV popup */}
        {exportOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setExportOpen(false)} data-testid="export-fees-modal">
            <div className="bg-white border border-zinc-300 shadow-xl w-[360px] p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#09090B]">Export College Data</h3>
                <button onClick={() => setExportOpen(false)} className="text-zinc-400 hover:text-[#09090B]"><X size={14} /></button>
              </div>
              <p className="text-[11px] text-[#52525B] mb-3">Exports the selected fields for the current filters. Turn an identity axis <b>Off</b> to export codes-only or aliases-only (at least one must stay on).</p>
              <div className="mb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] mb-1">Aliases</div>
                <div className="flex gap-2">
                  {["off", "single", "all"].map((o) => {
                    const disabled = o === "off" && exportCodes === "off";
                    return (
                      <button key={o} disabled={disabled}
                        onClick={() => { if (o === "off" && exportCodes === "off") return; setExportAliases(o); }}
                        data-testid={`export-aliases-${o}`}
                        className={`h-7 px-3 text-[10px] font-bold uppercase border disabled:opacity-40 ${exportAliases === o ? "bg-[#0EA5E9] text-white border-[#0EA5E9]" : "bg-white text-[#52525B] border-zinc-300"}`}>
                        {o === "off" ? "Off" : o === "single" ? "Primary only" : "All aliases"}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mb-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] mb-1">Codes</div>
                <div className="flex gap-2">
                  {["off", "single", "all"].map((o) => {
                    const disabled = o === "off" && exportAliases === "off";
                    return (
                      <button key={o} disabled={disabled}
                        onClick={() => { if (o === "off" && exportAliases === "off") return; setExportCodes(o); }}
                        data-testid={`export-codes-${o}`}
                        className={`h-7 px-3 text-[10px] font-bold uppercase border disabled:opacity-40 ${exportCodes === o ? "bg-[#0EA5E9] text-white border-[#0EA5E9]" : "bg-white text-[#52525B] border-zinc-300"}`}>
                        {o === "off" ? "Off" : o === "single" ? "Primary only" : "All codes"}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mb-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] mb-1">Fields to include</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                  {[["fees", "Fees"], ["seats", "Seats"], ["established_year", "Est. year"], ["college_status", "Status"]].map(([k, lbl]) => (
                    <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid={`export-field-${k}`}>
                      <input type="checkbox" checked={exportFields.has(k)}
                        onChange={() => setExportFields((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                        className="w-3.5 h-3.5 accent-[#0EA5E9] cursor-pointer" />
                      <span className={`text-[11px] font-semibold ${exportFields.has(k) ? "text-[#09090B]" : "text-zinc-400 line-through"}`}>{lbl}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400 mt-1">No fee field selected → one row per college (course/year & fee columns dropped).</p>
              </div>
              <button disabled={exporting} data-testid="export-fees-download-btn"
                onClick={async () => {
                  setExporting(true);
                  try {
                    const params = { aliases: exportAliases, codes: exportCodes };
                    if (exportFields.size) params.fields = Array.from(exportFields).join(",");
                    if (state !== "All") params.state = state;
                    if (course !== "all") params.course = course;
                    if (collegeTypes.length) params.college_type = collegeTypes.join(",");
                    if (feeBucket !== "none") params.fee_bucket = feeBucket;
                    if (spotlight !== "all") params.spotlight = spotlight;
                    if (search) params.search = search;
                    const res = await exportFeesCsv(params);
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                    const a = document.createElement("a");
                    const _order = ["fees", "seats", "established_year", "college_status"];
                    const _short = { fees: "fees", seats: "seats", established_year: "estyear", college_status: "status" };
                    const fieldTag = _order.filter((f) => exportFields.has(f)).map((f) => _short[f]).join("_") || "data";
                    a.href = url; a.download = `${fieldTag}_${state}_${Date.now()}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    setExportOpen(false);
                  } catch (e) { console.error(e); }
                  setExporting(false);
                }}
                className="w-full h-8 bg-[#09090B] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#27272A] disabled:opacity-50">
                {exporting ? "Exporting…" : "Download CSV"}
              </button>
            </div>
          </div>
        )}

        {/* Dedup Results Panel */}
        {dedupGroups !== null && (
          <div className="border border-[#FFC107] bg-[#FFC107]/5 mb-4" data-testid="dedup-panel">
            <div className="p-3 border-b border-[#FFC107]/30 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#92700C]">
                {dedupGroups.length > 0 ? `${dedupGroups.length} DUPLICATE GROUPS FOUND` : "NO DUPLICATES FOUND"}
              </span>
              <div className="flex items-center gap-2">
                {dedupGroups.length > 0 && (
                  <button onClick={async () => {
                      setMerging(true);
                      try {
                        for (const group of dedupGroups) {
                          const dups = group.members.filter(m => m.college_code !== group.master).map(m => m.college_code);
                          await mergeDuplicates(group.master, dups);
                        }
                        setDedupGroups([]);
                        loadColleges();
                      } catch (e) { console.error(e); }
                      setMerging(false);
                    }}
                    disabled={merging}
                    className="h-7 px-3 bg-[#6043EF] text-white text-[9px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50 flex items-center gap-1"
                    data-testid="merge-all-btn">
                    <ArrowsClockwise size={11} /> {merging ? "MERGING..." : "MERGE ALL"}
                  </button>
                )}
                <button onClick={() => setDedupGroups(null)} className="text-zinc-400 hover:text-[#09090B]"><X size={14} /></button>
              </div>
            </div>
            {dedupGroups.map((group) => (
              <div key={group.master} className="border-b border-[#FFC107]/20 p-3">
                <p className="text-xs font-bold text-[#09090B] mb-2">{group.name}</p>
                <div className="space-y-1">
                  {group.members.map((m) => {
                    const isMaster = m.college_code === group.master;
                    return (
                      <div key={m.college_code} className={`flex items-center gap-2 px-2 py-1.5 text-xs ${isMaster ? "bg-[#00C04B]/10 border-l-2 border-[#00C04B]" : "bg-[#FF2A00]/5 border-l-2 border-[#FF2A00]/30"}`}>
                        <span className={`font-bold ${isMaster ? "text-[#00C04B]" : "text-[#FF2A00]"}`}>{isMaster ? "MASTER" : "DUP"}</span>
                        <span className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400">{m.college_code}</span>
                        <span className="font-medium text-[#09090B] flex-1">
                          {m.short_name || m.primary_name}
                          {(m.city || m.location) && <span className="text-zinc-400 font-normal">, {m.city || m.location}</span>}
                        </span>
                        <span className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400" title="Allotments | Seat Matrices | Counselling Results">
                          {m.allotment_count}a {m.sm_count}sm {m.cr_count}cr {m.has_fees ? "fees" : ""}
                        </span>
                        <Badge variant="outline" className="text-[8px]">{m.source || "?"}</Badge>
                        {!isMaster && (
                          <button
                            onClick={() => setDedupGroups(prev => prev.map(g => g.master === group.master ? { ...g, master: m.college_code } : g))}
                            className="text-[8px] font-bold uppercase tracking-wider text-[#6043EF] hover:underline shrink-0"
                            data-testid={`set-master-${m.college_code}`}
                            title="Make this the master (merge the others into it)">
                            Make master
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button onClick={async () => {
                    setMerging(true);
                    const dups = group.members.filter(m => m.college_code !== group.master).map(m => m.college_code);
                    try {
                      await mergeDuplicates(group.master, dups);
                      setDedupGroups(prev => prev.filter(g => g.master !== group.master));
                      loadColleges();
                    } catch (e) { console.error(e); }
                    setMerging(false);
                  }}
                  disabled={merging}
                  className="mt-2 h-7 px-3 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50 flex items-center gap-1"
                  data-testid={`merge-group-${group.master}`}>
                  <ArrowsClockwise size={11} /> MERGE INTO MASTER
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Fill New Colleges Panel */}
        {fillList !== null && (
          <div className="border border-[#6043EF] bg-[#6043EF]/5 mb-4" data-testid="fill-new-panel">
            <div className="p-3 border-b border-[#6043EF]/30 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A28C2]">
                {fillList.length > 0 ? `${fillList.length} NEW COLLEGES · NOT YET ENRICHED` : "NO NEW COLLEGES TO FILL"}
              </span>
              <div className="flex items-center gap-2">
                {fillList.length > 0 && (
                  <button onClick={async () => {
                      setFillRunning(true);
                      const codes = fillList.map(c => c.college_code);
                      try {
                        const res = await runFillNewColleges(state, codes);
                        const jobs = res.data.jobs || {};
                        setFillJobs(jobs);
                        const init = {}; Object.keys(jobs).forEach(c => { init[c] = "queued"; });
                        setFillStatuses(init);
                        const jobIds = Object.values(jobs);
                        const poll = setInterval(async () => {
                          try {
                            const sres = await jobsStatus(jobIds);
                            const smap = sres.data.statuses || {};
                            const next = {};
                            Object.entries(jobs).forEach(([code, jid]) => { next[code] = smap[jid] || "queued"; });
                            setFillStatuses(next);
                            if (Object.values(next).every(s => s === "completed" || s === "failed")) {
                              clearInterval(poll);
                              setFillRunning(false);
                              loadColleges();
                              try { const p = await fillNewCollegesPreview(state); setFillList(p.data.colleges || []); } catch (e) {}
                            }
                          } catch (e) { /* keep polling */ }
                        }, 3000);
                      } catch (e) { console.error(e); setFillRunning(false); }
                    }}
                    disabled={fillRunning}
                    className="h-7 px-3 bg-[#6043EF] text-white text-[9px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50 flex items-center gap-1"
                    data-testid="fill-all-btn">
                    <Sparkle size={11} /> {fillRunning ? "FILLING..." : `FILL ALL ${fillList.length}`}
                  </button>
                )}
                <button onClick={() => setFillList(null)} className="text-zinc-400 hover:text-[#09090B]"><X size={14} /></button>
              </div>
            </div>
            <div className="p-2 space-y-1">
              {fillList.map((c) => {
                const st = fillStatuses[c.college_code];
                const busy = st === "queued" || st === "running";
                return (
                  <div key={c.college_code} className="flex items-center gap-2 px-2 py-1.5 text-xs bg-white border border-zinc-100" data-testid={`fill-row-${c.college_code}`}>
                    <span className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400">{c.college_code}</span>
                    <span className="font-medium text-[#09090B]">{c.name}</span>
                    {c.city && <span className="text-[10px] text-zinc-400 flex items-center gap-0.5"><MapPin size={10} />{c.city}</span>}
                    <div className="flex items-center gap-1 ml-auto">
                      {(c.missing || []).map(m => <Badge key={m} variant="outline" className="text-[8px] text-zinc-500">{m}</Badge>)}
                    </div>
                    <span className="w-14 text-right shrink-0" data-testid={`fill-status-${c.college_code}`}>
                      {busy && <CircleNotch size={13} className="animate-spin text-[#6043EF] inline" />}
                      {st === "completed" && <Check size={13} className="text-[#00C04B] inline" weight="bold" />}
                      {st === "failed" && <X size={13} className="text-[#FF2A00] inline" weight="bold" />}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-12 bg-zinc-200 animate-pulse" />
            ))}
          </div>
        ) : colleges.length === 0 ? (
          (() => {
            const activeFilters = [];
            if (state !== "All") activeFilters.push({ key: "state", label: `State: ${state}`, reset: () => { setState("All"); setPage(1); } });
            if (course !== "all") activeFilters.push({ key: "course", label: `Course: ${course}`, reset: () => { setCourse("all"); setPage(1); } });
            if (collegeTypes.length) activeFilters.push({ key: "type", label: `Types: ${collegeTypes.join(", ")}`, reset: () => { setCollegeTypes([]); setPage(1); } });
            if (feeBucket !== "none") activeFilters.push({ key: "fees", label: `Fees: ${FEE_BUCKET_LABELS[feeBucket]}`, reset: () => { setFeeBucket("none"); setPage(1); } });
            if (spotlight !== "all") activeFilters.push({ key: "spotlight", label: `Spotlight: ${spotlight}`, reset: () => { setSpotlight("all"); setPage(1); } });
            if (search) activeFilters.push({ key: "search", label: `Search: "${search}"`, reset: () => setSearch("") });
            return (
              <div className="border border-zinc-200 py-14 px-6 flex flex-col items-center text-center" data-testid="colleges-empty-state">
                <Buildings size={40} weight="light" className="text-zinc-300 mb-3" />
                <h3 className="text-lg font-bold text-[#09090B]">No colleges match these filters</h3>
                {activeFilters.length > 0 ? (
                  <>
                    <p className="text-sm text-[#52525B] mt-1 mb-5">Try broadening your search — remove one of the filters below:</p>
                    <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl">
                      {activeFilters.map((f) => (
                        <button
                          key={f.key}
                          onClick={f.reset}
                          className="group flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#09090B] bg-zinc-100 border border-zinc-200 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors"
                          data-testid={`relax-filter-${f.key}`}
                        >
                          {f.label}
                          <X size={12} weight="bold" className="text-zinc-400 group-hover:text-[#6043EF]" />
                        </button>
                      ))}
                    </div>
                    {activeFilters.length > 1 && (
                      <button
                        onClick={() => { setState("All"); setCourse("all"); setCollegeTypes([]); setFeeBucket("none"); setSpotlight("all"); setSearch(""); setPage(1); }}
                        className="mt-5 text-xs font-bold uppercase tracking-wider text-[#6043EF] hover:underline"
                        data-testid="relax-clear-all"
                      >
                        Clear all filters
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-[#52525B] mt-1">No colleges available right now.</p>
                )}
              </div>
            );
          })()
        ) : (
          <div className="border border-zinc-200 overflow-x-auto" data-testid="colleges-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                  <TableHead className="hidden md:table-cell w-10 font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right pr-1">#</TableHead>
                  <TableHead className="w-6"></TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] w-[240px] md:w-[520px]">COLLEGE</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">CITY</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">TYPE</TableHead>
                  {feeBucket !== "none" && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right whitespace-nowrap" data-testid="fees-col-head">{FEE_BUCKET_LABELS[feeBucket]}</TableHead>}
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">SEATS</TableHead>
                  {feeBucket === "none" && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">COUNSELLING</TableHead>}
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[180px]">STATUS</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">SOURCE</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {colleges.map((c, i) => (
                  <CollegeRow key={c.id || i} c={c} i={i} feeBucket={feeBucket}
                    isAdmin={isAdmin} onAddFees={() => setFeesDialog(c)} onAiFees={() => setAiFeesDialog(c)}
                    onOfficialFees={() => setOfficialFeesDialog(c)}
                    expanded={expandedRow === i}
                    onToggle={() => setExpandedRow(expandedRow === i ? null : i)} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Infinite scroll sentinel + status */}
        {!loading && colleges.length > 0 && (
          <div className="mt-4" data-testid="infinite-scroll">
            <div ref={loadMoreRef} className="h-px" aria-hidden="true" />
            {loadingMore && (
              <div className="flex items-center justify-center gap-2 py-4 text-xs font-bold uppercase tracking-wider text-[#52525B]" data-testid="infinite-loading">
                <ArrowsClockwise size={14} className="animate-spin" /> Loading more…
              </div>
            )}
            <p className="text-center font-['IBM_Plex_Mono'] text-xs text-[#52525B] py-2" data-testid="infinite-status">
              Showing {colleges.length} of {total}{colleges.length >= total && total > 0 ? " — end of list" : ""}
            </p>
          </div>
        )}
      </div>
      {feesDialog && (
        <FeesDialog
          college={feesDialog}
          onClose={() => setFeesDialog(null)}
          onSaved={() => { setFeesDialog(null); loadColleges(1, false); }}
        />
      )}
      {aiFeesDialog && (
        <AiFeesDialog
          college={aiFeesDialog}
          onClose={() => setAiFeesDialog(null)}
          onSaved={() => { setAiFeesDialog(null); loadColleges(1, false); }}
        />
      )}
      {officialFeesDialog && (
        <OfficialFeesDialog
          college={officialFeesDialog}
          onClose={() => setOfficialFeesDialog(null)}
          onSaved={() => { setOfficialFeesDialog(null); loadColleges(1, false); }}
        />
      )}
    </div>
  );
}


const COURSE_LABELS = {
  MBBS: "MBBS",
  BDS: "BDS (Dental)",
  BHMS: "BHMS (Homeopathy)",
  BSc_Nursing: "B.Sc Nursing",
  BSc_Poultry: "B.Sc Poultry",
  BVSc: "B.V.Sc (Veterinary)",
};

const TYPE_BADGE = {
  govt: "bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B]",
  private: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]",
  trust: "bg-[#92700C]/10 text-[#92700C] border-[#92700C]",
  deemed: "bg-[#6043EF]/10 text-[#6043EF] border-[#6043EF]",
  "": "bg-zinc-100 text-zinc-500 border-zinc-300",
};

function CollegeLogo({ code, hasLogo, name }) {
  return <SharedCollegeLogo code={code} hasLogo={hasLogo} name={name} />;
}

const FEE_FIELDS = [
  { key: "govt_fees", label: "Govt / State-quota" },
  { key: "regular_fees", label: "Private / Management" },
  { key: "nri_fees", label: "NRI-quota" },
  { key: "hostel_fees", label: "Hostel (annual)" },
];
// Manual entry aligns 1:1 with the list's fee-column buckets (Private and Management are separate).
const MANUAL_FEE_FIELDS = [
  { key: "govt_fees", label: "Govt / State-quota" },
  { key: "private_fees", label: "Private" },
  { key: "other_fees", label: "Management / Other" },
  { key: "nri_fees", label: "NRI-quota" },
  { key: "hostel_fees", label: "Hostel (annual)" },
];

// Fee-origin badge. Authority: PDF (ingested) > Manual > Official (scraped) > AI.
const FEE_SOURCE_META = {
  pdf:      { label: "PDF",      cls: "bg-emerald-100 text-emerald-700", title: "From official PDF/CSV ingestion — most authoritative" },
  manual:   { label: "Manual",   cls: "bg-[#6043EF]/10 text-[#6043EF]", title: "Manually entered by an admin" },
  official: { label: "Official", cls: "bg-sky-100 text-sky-700",         title: "Scraped from the college's official website" },
  ai:       { label: "AI",       cls: "bg-amber-100 text-amber-700",     title: "AI-suggested (least authoritative)" },
};
function FeeSourceBadge({ source }) {
  const m = FEE_SOURCE_META[source];
  if (!m) return null;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider ${m.cls}`}
      title={m.title} data-testid={`fee-source-${source}`}>
      {m.label}
    </span>
  );
}

function FeesDialog({ college, onClose, onSaved }) {
  const courses = (college.courses && college.courses.length ? college.courses : ["MBBS"]);
  const [course, setCourse] = useState(courses[0]);
  const [year, setYear] = useState("2026-27");
  const [vals, setVals] = useState({ govt_fees: "", private_fees: "", other_fees: "", nri_fees: "", hostel_fees: "" });
  const [nriCurrency, setNriCurrency] = useState("INR");
  const [saving, setSaving] = useState(false);

  // Pre-fill the form with the college's EXISTING fees for the chosen course + year,
  // so admins see/edit current values instead of a blank (all-0) form.
  useEffect(() => {
    const fcd = college.fees_csv_data || {};
    const yb = fcd[year] || {};
    const ck = Object.keys(yb).find((k) => String(k).toUpperCase() === String(course).toUpperCase());
    const b = yb[ck] || (Object.keys(yb).length === 1 ? yb[Object.keys(yb)[0]] : null) || {};
    const num = (v) => (v || v === 0) && Number(v) > 0 ? String(v) : "";
    setVals({
      govt_fees: num(b.govt_fees),
      private_fees: num(b.private_fees ?? b.regular_fees),
      other_fees: num(b.other_fees),
      nri_fees: num(b.nri_fees),
      hostel_fees: num(b.hostel_fees ?? b.extra?.hostel_fees),
    });
    setNriCurrency(b.nri_currency === "USD" ? "USD" : "INR");
    // eslint-disable-next-line
  }, [course, year]);

  const setVal = (k, v) => setVals((s) => ({ ...s, [k]: v.replace(/[^\d]/g, "") }));
  const hasAny = Object.values(vals).some((v) => v && Number(v) > 0);

  const submit = async () => {
    if (!hasAny) { toast.error("Enter at least one fee amount"); return; }
    setSaving(true);
    try {
      const payload = { college_code: college.college_code || college.id, year, course, nri_currency: nriCurrency };
      MANUAL_FEE_FIELDS.forEach(({ key }) => { if (vals[key]) payload[key] = Number(vals[key]); });
      const { data } = await addMasterFees(payload);
      if (data?.applied) {
        toast.success(`Fees added for ${college.name}`);
        onSaved();
      } else {
        toast.error(data?.message || "Could not add fees");
        setSaving(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add fees");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="add-fees-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Add Fees — {college.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Course</label>
              <Select value={course} onValueChange={setCourse}>
                <SelectTrigger className="h-9 mt-1" data-testid="fees-course-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {courses.map((cr) => <SelectItem key={cr} value={cr}>{COURSE_LABELS[cr] || cr}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Academic Year</label>
              <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026-27"
                className="h-9 mt-1 font-['IBM_Plex_Mono']" data-testid="fees-year-input" />
            </div>
          </div>
          <div className="space-y-2">
            {MANUAL_FEE_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <label className="text-xs text-[#52525B] w-40 shrink-0">{label}</label>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-zinc-400 text-sm">{key === "nri_fees" && nriCurrency === "USD" ? "$" : "₹"}</span>
                  <Input value={vals[key]} onChange={(e) => setVal(key, e.target.value)} placeholder="0"
                    inputMode="numeric" className="h-8 font-['IBM_Plex_Mono']" data-testid={`fees-input-${key}`} />
                  {key === "nri_fees" && (
                    <div className="flex rounded-md border border-zinc-200 overflow-hidden shrink-0">
                      {["INR", "USD"].map((cur) => (
                        <button key={cur} type="button" onClick={() => setNriCurrency(cur)}
                          className={`px-2 h-8 text-[10px] font-bold ${nriCurrency === cur ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
                          data-testid={`fees-nri-${cur}`}>{cur}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400">Only filled amounts are saved. Values merge into the {year} {course} bucket, tagged as manually entered.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="fees-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !hasAny} className="bg-[#6043EF] hover:bg-[#4f36c9]" data-testid="fees-save-btn">
            {saving ? "Saving…" : "Save Fees"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AiFeesDialog({ college, onClose, onSaved }) {
  const code = college.college_code || college.id;
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null); // {proposed:{...}, nri_currency, name, year}
  const [vals, setVals] = useState({ govt_fees: "", regular_fees: "", nri_fees: "", hostel_fees: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await feesLlmCheck([code]);
        const r = (data.results || [])[0] || {};
        const p = r.proposed || {};
        if (!alive) return;
        setProposal({ name: r.name || college.name, nri_currency: r.nri_currency || "INR", year: data.year });
        setVals({
          govt_fees: p.govt_fees ? String(p.govt_fees) : "",
          regular_fees: p.regular_fees ? String(p.regular_fees) : "",
          nri_fees: p.nri_fees ? String(p.nri_fees) : "",
          hostel_fees: p.hostel_fees ? String(p.hostel_fees) : "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "AI fee lookup failed");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code]); // eslint-disable-line

  const setVal = (k, v) => setVals((s) => ({ ...s, [k]: v.replace(/[^\d]/g, "") }));
  const hasAny = Object.values(vals).some((v) => v && Number(v) > 0);

  const apply = async () => {
    if (!hasAny) { toast.error("Nothing to apply"); return; }
    setSaving(true);
    try {
      const fees = {};
      FEE_FIELDS.forEach(({ key }) => { if (vals[key]) fees[key] = Number(vals[key]); });
      const { data } = await feesLlmApply([{ college_code: code, fees, nri_currency: proposal?.nri_currency || "INR" }]);
      if (data?.applied) {
        toast.success(`AI fees applied for ${proposal?.name || college.name}`);
        onSaved();
      } else {
        toast.error("Could not apply fees");
        setSaving(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply fees");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ai-fees-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#92700C]" /> AI Fees — {college.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-[#52525B]" data-testid="ai-fees-loading">
            <CircleNotch size={18} className="animate-spin" /> Looking up published fees…
          </div>
        ) : (
          <>
            <div className="space-y-4 py-1">
              {!hasAny && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="ai-fees-empty">
                  The AI could not confidently find published fees for this college. You can enter values manually below or cancel.
                </div>
              )}
              <div className="space-y-2">
                {FEE_FIELDS.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <label className="text-xs text-[#52525B] w-40 shrink-0">{label}</label>
                    <div className="flex items-center gap-1 flex-1">
                      <span className="text-zinc-400 text-sm">{key === "nri_fees" && proposal?.nri_currency === "USD" ? "$" : "₹"}</span>
                      <Input value={vals[key]} onChange={(e) => setVal(key, e.target.value)} placeholder="—"
                        inputMode="numeric" className="h-8 font-['IBM_Plex_Mono']" data-testid={`ai-fees-input-${key}`} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-zinc-400">AI-suggested values for {proposal?.year || "the latest year"}. Review/edit, then apply. Saved values are tagged AI-sourced.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="ai-fees-cancel-btn">Cancel</Button>
              <Button onClick={apply} disabled={saving || !hasAny} className="bg-[#92700C] hover:bg-[#7a5d0a] text-white" data-testid="ai-fees-apply-btn">
                {saving ? "Applying…" : "Apply Fees"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OfficialFeesDialog({ college, onClose, onSaved }) {
  const code = college.college_code || college.id;
  const courses = (college.courses && college.courses.length ? college.courses : ["MBBS"]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null); // {name, source_urls, from_cache, nri_currency}
  const [status, setStatus] = useState(null); // non-ok status string
  const [year, setYear] = useState("2026-27");
  const [course, setCourse] = useState(courses[0]);
  const [vals, setVals] = useState({ govt_fees: "", regular_fees: "", nri_fees: "", hostel_fees: "" });
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null); // {stage, url}
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  const run = async (force) => {
    setLoading(true); setStatus(null); setProgress(null);
    try {
      const { data: q } = await fetchFeesOfficial({ college_code: code, force, year, course });
      const jobId = q.job_id;
      // poll the background job (scrape + LLM can exceed the 60s ingress limit)
      const poll = async () => {
        const { data: job } = await fetchFeesOfficialJob(jobId);
        if (job.progress) setProgress({ stage: job.progress.stage, url: job.progress.url });
        if (job.status === "completed") {
          const data = job.result || {};
          if (data.status !== "ok") { setStatus(data.status || "error"); setMeta({ name: data.name || college.name }); setLoading(false); return; }
          const p = data.proposal || {};
          setMeta({ name: data.name, source_urls: data.source_urls || [], from_cache: !!data.from_cache, nri_currency: data.nri_currency || "INR" });
          if (data.course) setCourse(data.course);
          setVals({
            govt_fees: p.govt_fees ? String(p.govt_fees) : "",
            regular_fees: p.regular_fees ? String(p.regular_fees) : "",
            nri_fees: p.nri_fees ? String(p.nri_fees) : "",
            hostel_fees: p.hostel_fees ? String(p.hostel_fees) : "",
          });
          setLoading(false);
          return;
        }
        if (job.status === "failed" || job.status === "cancelled") {
          setStatus("error"); setMeta({ name: college.name }); setLoading(false); return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Official fee scrape failed");
      onClose();
    }
  };
  useEffect(() => { run(false); }, [code]); // eslint-disable-line

  const setVal = (k, v) => setVals((s) => ({ ...s, [k]: v.replace(/[^\d]/g, "") }));
  const hasAny = Object.values(vals).some((v) => v && Number(v) > 0);

  const apply = async () => {
    if (!hasAny) { toast.error("Nothing to apply"); return; }
    setSaving(true);
    try {
      const fees = {};
      FEE_FIELDS.forEach(({ key }) => { if (vals[key]) fees[key] = Number(vals[key]); });
      const { data } = await fetchFeesOfficial({
        college_code: code, apply: true, fees, year, course,
        nri_currency: meta?.nri_currency || "INR", source_urls: meta?.source_urls || [],
      });
      if (data?.applied) { toast.success(`Official fees applied for ${meta?.name || college.name}`); onSaved(); }
      else { toast.error("Could not apply fees"); setSaving(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply fees");
      setSaving(false);
    }
  };

  const STATUS_MSG = {
    no_website: "This college has no official website on record — add one first, then retry.",
    unreachable: "The official website could not be reached (down or blocking crawlers).",
    fetch_error: "Something went wrong reaching the official website.",
    not_found: "College not found.",
    error: "Could not extract fees from the official site.",
  };

  // Manually add a fee-source URL the crawler's regex missed → cache it → re-fetch.
  const [addUrl, setAddUrl] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const submitFeeUrl = async () => {
    const u = addUrl.trim();
    if (!u) return;
    setAddingUrl(true);
    try {
      const { data } = await addFeeSourceUrl(code, u);
      if (data?.ok) {
        toast.success("Fee page cached — re-reading fees…");
        setAddUrl(""); setAddOpen(false);
        run(false); // re-extract; now includes the newly cached page
      } else {
        toast.error(data?.message || "Could not fetch that URL");
        setAddingUrl(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that URL");
      setAddingUrl(false);
    }
  };
  const feeUrlAdder = addOpen ? (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5" data-testid="official-fees-add-url">
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Found the fee page yourself? Add its URL</label>
        <button onClick={() => { setAddOpen(false); setAddUrl(""); }} className="text-zinc-400 hover:text-zinc-700" data-testid="official-fees-url-close"><X size={13} /></button>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Input value={addUrl} autoFocus onChange={(e) => setAddUrl(e.target.value)} placeholder="https://college.edu/fees"
          className="h-8 text-xs font-['IBM_Plex_Mono']" data-testid="official-fees-url-input"
          onKeyDown={(e) => { if (e.key === "Enter") submitFeeUrl(); if (e.key === "Escape") { setAddOpen(false); setAddUrl(""); } }} />
        <Button onClick={submitFeeUrl} disabled={addingUrl || !addUrl.trim()} className="h-8 bg-[#0369A1] hover:bg-[#025585] text-white shrink-0 text-xs" data-testid="official-fees-url-add-btn">
          {addingUrl ? "Caching…" : "Add & re-read"}
        </Button>
      </div>
      <p className="text-[10px] text-zinc-400 mt-1">We'll fetch &amp; cache that page, then re-read fees from it. It's reused on future runs too.</p>
    </div>
  ) : (
    <button onClick={() => setAddOpen(true)}
      className="inline-flex items-center gap-1 h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider text-[#0369A1] border border-dashed border-[#0369A1]/40 rounded-md hover:bg-[#0369A1]/5 transition-colors"
      data-testid="official-fees-add-url-btn">
      <Plus size={12} weight="bold" /> Add fee source
    </button>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="official-fees-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Globe size={16} weight="bold" className="text-[#0369A1]" /> Official Fees — {college.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm text-[#52525B]" data-testid="official-fees-loading">
            <div className="relative w-9 h-9">
              {[
                <CircleNotch key="l" size={30} weight="bold" className="text-[#0369A1] animate-spin" />,
                <CurrencyInr key="f" size={30} weight="bold" className="text-emerald-600" />,
                <Buildings key="c" size={30} weight="bold" className="text-[#6043EF]" />,
              ].map((icon, idx) => (
                <span key={idx}
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${tick % 3 === idx ? "opacity-100" : "opacity-0"}`}>
                  {icon}
                </span>
              ))}
            </div>
            <span>{progress?.stage || "Scraping official site for fees…"}</span>
            {progress?.url && (
              <span className="text-[10px] font-['IBM_Plex_Mono'] text-[#0369A1] max-w-[90%] truncate" title={progress.url} data-testid="official-fees-progress-url">
                {progress.url}
              </span>
            )}
          </div>
        ) : status ? (
          <div className="py-6 space-y-3">
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800" data-testid="official-fees-status">
              {STATUS_MSG[status] || status}
            </div>
            {feeUrlAdder}
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={onClose} data-testid="official-fees-close-btn">Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Course</label>
                  <Select value={course} onValueChange={setCourse}>
                    <SelectTrigger className="h-9 mt-1" data-testid="official-fees-course-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {courses.map((cr) => <SelectItem key={cr} value={cr}>{COURSE_LABELS[cr] || cr}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Academic Year</label>
                  <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026-27"
                    className="h-9 mt-1 font-['IBM_Plex_Mono']" data-testid="official-fees-year-input" />
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-2 text-[10px]">
                {meta?.from_cache && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider" data-testid="official-fees-cache-badge">from cache</span>}
                <button onClick={() => run(false)} className="inline-flex items-center gap-1 text-[#0369A1] font-bold uppercase tracking-wider hover:underline" data-testid="official-fees-refetch">
                  <ArrowsClockwise size={11} weight="bold" /> Re-fetch for this course
                </button>
                <button onClick={() => run(true)} className="ml-auto inline-flex items-center gap-1 text-[#0369A1] font-bold uppercase tracking-wider hover:underline" data-testid="official-fees-rescrape">
                  <ArrowsClockwise size={11} weight="bold" /> Re-scrape (ignore cache)
                </button>
              </div>
              {!hasAny && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="official-fees-empty">
                  No fees were found on the official pages we scraped. You can enter values manually, re-scrape, or cancel.
                </div>
              )}
              <div className="space-y-2">
                {FEE_FIELDS.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2">
                    <label className="text-xs text-[#52525B] w-40 shrink-0">{label}</label>
                    <div className="flex items-center gap-1 flex-1">
                      <span className="text-zinc-400 text-sm">{key === "nri_fees" && meta?.nri_currency === "USD" ? "$" : "₹"}</span>
                      <Input value={vals[key]} onChange={(e) => setVal(key, e.target.value)} placeholder="—"
                        inputMode="numeric" className="h-8 font-['IBM_Plex_Mono']" data-testid={`official-fees-input-${key}`} />
                    </div>
                  </div>
                ))}
              </div>
              {meta?.source_urls?.length > 0 && (
                <div className="text-[10px] text-zinc-400">
                  <span className="font-bold uppercase tracking-wider">Sources:</span>
                  <ul className="mt-1 space-y-0.5">
                    {meta.source_urls.slice(0, 5).map((u, k) => (
                      <li key={k}><a href={u} target="_blank" rel="noreferrer" className="text-[#0369A1] hover:underline inline-flex items-center gap-1 break-all"><ArrowSquareOut size={9} /> {u}</a></li>
                    ))}
                  </ul>
                </div>
              )}
              {feeUrlAdder}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="official-fees-cancel-btn">Cancel</Button>
              <Button onClick={apply} disabled={saving || !hasAny} className="bg-[#0369A1] hover:bg-[#025585] text-white" data-testid="official-fees-apply-btn">
                {saving ? "Applying…" : "Apply Fees"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CollegeRow({ c, i, expanded, onToggle, feeBucket, isAdmin, onAddFees, onAiFees, onOfficialFees }) {
  const aliases = c.aliases || [];
  const detailPath = c.seo_path ? `/colleges/${c.seo_path}` : `/colleges/${c.college_code || c.id}`;
  const src = (c.source || "").toLowerCase();
  const si = c.seats_info || {};
  const bd = si.breakdown || {};
  const isIngest = src.startsWith("ingest:");
  const currentYear = new Date().getFullYear();
  // Two ingest-stub states (matches the Fill-New candidate rule
  // `ingestion_common.find_unfilled_new_colleges`):
  //  • CREATED (red)  — source `ingest:*`, NOT yet enriched → needs Dedup / Fill New.
  //  • ADDED  (green) — enriched (`details_filled_at`) AND this year's ingest →
  //    a genuinely NEW college added this season. Prior-year filled stubs get no tag.
  const flag = isIngest && !c.details_filled_at ? "created"
             : isIngest && c.details_filled_at && Number(si.year) === currentYear ? "added"
             : null;
  const isCreated = flag === "created";
  const stampKind = [("seat_matrix" in bd) && "Seat Matrix", ("allotments" in bd) && "Allotment"]
    .filter(Boolean).join(" + ") || (si.source === "allotments" ? "Allotment" : si.source === "seat_matrix" ? "Seat Matrix" : "SM / Allotment");
  const stampBody = c.counselling_body || (c.source || "").replace(/^ingest:/i, "").replace(/_/g, " ") || "—";
  const createdOn = c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB") : null;
  const addedOn = c.details_filled_at ? new Date(c.details_filled_at).toLocaleDateString("en-GB") : null;
  const flagColor = isCreated ? "#E11D48" : "#059669";
  const flagLabel = isCreated ? "Created" : "Added";
  const flagBadge = isCreated ? "Auto-created" : "Newly added";
  const flagBlurb = isCreated
    ? <>Created from an <b>unmatched</b> ingestion row — needs matchmaking (Dedup) or Fill New.</>
    : <>A <b>new college</b> added this season from {stampKind} ingestion, now enriched.</>;
  return (
    <>
      <TableRow className="hover:bg-zinc-50 cursor-pointer transition-colors duration-150" onClick={onToggle} data-testid={`college-row-${i}`}>
        <TableCell className="hidden md:table-cell w-10 text-right pr-1 relative font-['IBM_Plex_Mono'] text-sm font-bold text-[#3F3F46]" data-testid={`college-sr-${i}`}>
          {flag && (
            <HoverCard openDelay={80} closeDelay={40}>
              <HoverCardTrigger asChild>
                <span
                  onClick={(e) => e.stopPropagation()}
                  style={{ backgroundColor: flagColor }}
                  className="absolute -top-1 left-0 z-10 -rotate-12 origin-top-left select-none text-white text-[8px] font-black italic uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm shadow-sm cursor-help"
                  data-testid={`college-${flag}-tag-${i}`}>
                  {flagLabel}
                </span>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" className="w-64 p-3 text-xs" data-testid={`college-${flag}-tooltip-${i}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span style={{ backgroundColor: flagColor }} className="inline-block text-white text-[8px] font-black italic uppercase tracking-wider px-1.5 py-0.5 rounded-sm">{flagBadge}</span>
                </div>
                <p className="text-[11px] text-[#52525B] mb-2 leading-snug">{flagBlurb}</p>
                <dl className="space-y-1">
                  <div className="flex justify-between gap-2"><dt className="text-[#71717A] uppercase text-[9px] font-bold tracking-wider">Ingest</dt><dd className="font-medium text-[#09090B] text-right">{stampKind}</dd></div>
                  <div className="flex justify-between gap-2"><dt className="text-[#71717A] uppercase text-[9px] font-bold tracking-wider">Year</dt><dd className="font-medium text-[#09090B] text-right">{si.year || "—"}</dd></div>
                  <div className="flex justify-between gap-2"><dt className="text-[#71717A] uppercase text-[9px] font-bold tracking-wider">Body</dt><dd className="font-medium text-[#09090B] text-right">{stampBody}</dd></div>
                  {isCreated
                    ? (createdOn && <div className="flex justify-between gap-2"><dt className="text-[#71717A] uppercase text-[9px] font-bold tracking-wider">Created</dt><dd className="font-medium text-[#09090B] text-right">{createdOn}</dd></div>)
                    : (addedOn && <div className="flex justify-between gap-2"><dt className="text-[#71717A] uppercase text-[9px] font-bold tracking-wider">Added</dt><dd className="font-medium text-[#09090B] text-right">{addedOn}</dd></div>)}
                </dl>
                {si.note && <p className="mt-2 pt-2 border-t border-zinc-100 text-[10px] text-[#71717A] leading-snug">{si.note}</p>}
              </HoverCardContent>
            </HoverCard>
          )}
          {i + 1}
        </TableCell>
        <TableCell className="w-6 px-2">
          <CaretDown size={10} weight="bold" className={`text-zinc-400 transition-transform duration-150 ${expanded ? "" : "-rotate-90"}`} />
        </TableCell>
        <TableCell className="font-medium text-sm w-[240px] max-w-[240px] whitespace-normal align-top md:w-[520px] md:max-w-[520px] md:whitespace-nowrap md:align-middle" data-testid={`college-name-${i}`}>
          <div className="flex items-start md:items-center gap-2.5 min-w-0">
            <CollegeLogo code={c.college_code || c.id} hasLogo={!!c.logo_path} name={c.name} />
            <span className="flex items-center gap-1.5 flex-wrap md:flex-nowrap min-w-0">
              <Link to={detailPath} onClick={(e) => e.stopPropagation()} title={c.name} className="text-[#09090B] hover:text-[#6043EF] transition-colors break-words md:truncate min-w-0" data-testid={`college-link-${i}`}>
                {c.name}
              </Link>
              {c.national_importance && (
                <span title="Institute of National Importance" className="inline-flex items-center gap-0.5 bg-[#6043EF] text-white text-[8px] font-bold uppercase tracking-wider px-1 py-0.5" data-testid={`college-ni-${i}`}>
                  <SealCheck size={9} weight="fill" /> INI
                </span>
              )}
              {c.rank_highlight && (
                <span title={c.rank_highlight} className="inline-flex items-center gap-0.5 border border-[#92700C] text-[#92700C] text-[8px] font-bold uppercase tracking-wider px-1 py-0.5" data-testid={`college-rank-${i}`}>
                  <Trophy size={9} weight="fill" /> {c.rank_highlight}
                </span>
              )}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-xs text-[#52525B] max-w-[140px] truncate" data-testid={`college-city-${i}`} title={c.location || c.city || ""}>
          {(() => {
            const loc = c.location || c.city || "";
            if (!loc) return "—";
            // Show just first part (city/town) - truncate at first comma or 30 chars
            const firstPart = loc.split(',')[0].trim();
            return firstPart.length > 30 ? firstPart.substring(0, 28) + "…" : firstPart;
          })()}
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${TYPE_BADGE[c.college_type] || TYPE_BADGE[""]}`}>
            {c.college_type || "—"}
          </Badge>
        </TableCell>
        {feeBucket !== "none" && (
          <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm whitespace-nowrap" data-testid={`college-fees-${i}`}>
            {c.selected_fee ? (
              <span className="inline-flex items-center gap-1.5 justify-end">
                <span>{`${c.selected_fee_currency === "USD" ? "$" : "₹"}${Number(c.selected_fee).toLocaleString(c.selected_fee_currency === "USD" ? "en-US" : "en-IN")}`}</span>
                <FeeSourceBadge source={c.selected_fee_source} />
              </span>
            ) : isAdmin ? (
              <span className="inline-flex items-center gap-1.5 justify-end">
                <button
                  onClick={(e) => { e.stopPropagation(); onAddFees(); }}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#FAFAFA] text-[#6043EF] text-[10px] font-bold uppercase tracking-wider shadow-sm hover:shadow-md hover:bg-white transition-all"
                  data-testid={`add-fees-btn-${i}`}
                  title="Manually enter fees for this college"
                >
                  <Plus size={11} weight="bold" /> Fees
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onAiFees(); }}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#FAFAFA] text-[#92700C] text-[10px] font-bold uppercase tracking-wider shadow-sm hover:shadow-md hover:bg-white transition-all"
                  data-testid={`ai-fees-btn-${i}`}
                  title="Fetch fees via AI (LLM knowledge) — preview before applying"
                >
                  <Sparkle size={11} weight="fill" /> AI
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onOfficialFees(); }}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#FAFAFA] text-[#0369A1] text-[10px] font-bold uppercase tracking-wider shadow-sm hover:shadow-md hover:bg-white transition-all"
                  data-testid={`official-fees-btn-${i}`}
                  title="Fetch fees by scraping the college's official website — preview before applying"
                >
                  <Globe size={11} weight="bold" /> Official
                </button>
              </span>
            ) : (
              <span className="text-zinc-300">—</span>
            )}
          </TableCell>
        )}
        <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm" title={c.seats_info?.note || ""} data-testid={`college-seats-${i}`}>{c.total_seats || "—"}</TableCell>
        {feeBucket === "none" && <TableCell className="text-xs text-[#52525B]">{c.counselling_body}</TableCell>}
        <TableCell className="text-xs text-[#52525B] max-w-[200px]" data-testid={`college-status-${i}`}>
          {c.college_status ? (
            <span title={c.affiliate_university || ""}>
              <span className={`font-semibold ${/deem/i.test(c.college_status) ? "text-[#6043EF]" : "text-[#52525B]"}`}>{c.college_status}</span>
              {c.affiliate_university && <span className="text-zinc-400"> ({c.affiliate_university})</span>}
            </span>
          ) : (c.affiliate_university ? <span className="text-zinc-400 truncate">{c.affiliate_university}</span> : "—")}
        </TableCell>
        <TableCell className="text-[10px] text-zinc-400" title={c.seats_info?.note || ""}>
          {(() => {
            const s = c.seats_info?.source;
            if (s === "nmc") return "NMC";
            if (s === "seat_matrix") return "Matrix";
            if (s === "allotments") return "Allot";
            return "—";
          })()}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-zinc-50 hover:bg-zinc-50">
          <TableCell colSpan={9} className="p-0">
            <div className="px-8 py-3 animate-fade-in space-y-2">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="font-['IBM_Plex_Mono'] text-xs font-bold text-[#6043EF]">{c.college_code || c.id}</span>
                {c.location && (
                  <span className="flex items-center gap-1 text-xs text-[#52525B]">
                    <MapPin size={12} weight="bold" /> {c.location}
                  </span>
                )}
                {c.state && (
                  <span className="text-xs text-[#52525B]">{c.state}</span>
                )}
                <Link to={detailPath} className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#6043EF] hover:underline" data-testid={`college-detail-link-${i}`}>
                  VIEW DETAILS →
                </Link>
              </div>
              {aliases.length > 0 && (
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#52525B]">ALSO KNOWN AS:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {aliases.map((a, ai) => (
                      <span key={ai} className="border border-zinc-200 px-2 py-0.5 text-[10px] font-['IBM_Plex_Mono'] text-[#52525B]">
                        {a.name} <span className="text-zinc-400">({a.source})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
